import { memo, useEffect, useLayoutEffect, useMemo, useReducer, useRef, useState, useSyncExternalStore, type MouseEvent, type ReactElement, type RefObject } from "react";
import { CharacterWindow, type HudCharacter } from "../character/window.tsx";
import { levelRow } from "odyssey-shared";
import { FlagMark, Glyph, HudFrame, IconButton, KeyGlyph, MessageLine, flagWords, logical } from "../../ui/kit/index.ts";
import { PAGE_RULES } from "../rules.ts";
import { KIT_METRICS } from "../../ui/kit/metrics.ts";
import { maskPath } from "../../ui/kit/small-digits.ts";
import { diagnosticsRoom, slotDrawn, slotRect } from "./area-slots.ts";
import { bagButtonRect } from "./touch-slots.ts";
import { BUTTON_ICONS, BUTTON_ICON_SIZE } from "./button-icons.ts";
import { pageKeyHints } from "../touch/key-hints.ts";
import type { DiagnosticsSource } from "./diagnostics.ts";
import { flagCountdown } from "./flag-countdown.ts";
import { flagLockReason, switchCountdown, type HudSwitch } from "./flag-toggle.ts";
import { FlagTogglePanel } from "./flag-toggle-panel.tsx";
import { HUD_SLOTS, MENU_KEY, MENU_LAYOUT, MESSAGE_MAX_LINES, VITALS, VITALS_CARD, framedWidth, resolveSlot, type HudArea } from "./slots.ts";
import { TargetFrame, targetFrameRect } from "./target-frame.tsx";
import { flagOf, type HudSource, type HudVitals } from "./store.ts";
import { COMBAT_SLOT, fitName, holdFlashPhase, levelText, messageShown, nameRoom, vitalsView, type HealthBar, type VitalBar } from "./vitals.ts";
import { ConnectionMark, statusUnderMark } from "./connection-mark.tsx";
import { MaintenanceSlots } from "./maintenance-slots.tsx";
import { AbilityBar, CastBar, StatusRows, targetRowLeft } from "./combat-hud.tsx";
import { HarvestBar, type HudHarvest } from "../harvest/bar.tsx";
import { TravelBar } from "../travel/bar.tsx";
import { XpBar } from "./xp-bar.tsx";
import { ToastColumn, type HudLoot } from "./toast-column.tsx";
import { StanceButton, type StanceSource } from "./stance-button.tsx";
import { ToolBeltView } from "../belt/tool-belt.tsx";
import type { DiscoveryToasts } from "./discovery.ts";
import type { LevelUpToast } from "./level-up.ts";
import type { BarSource } from "../bar/store.ts";
import type { BarTicker } from "../bar/ticker.ts";
import type { TipController } from "../bar/tips.ts";
import { useTipHandlers } from "../bar/tip-handlers.ts";
import { TipsContext } from "../bar/hover-words.tsx";
import { NAME_TIP_ANCHOR } from "./name-tip.ts";
import { HudTooltip } from "../bar/tooltip-view.tsx";
import type { MaintenanceWarnings } from "./maintenance-warning.ts";
import { PerfPanel, type PerfSource } from "../perf/panel.tsx";
import { BagWindow, type HudBag } from "../bag/window.tsx";
import { EmoteWheel, type HudEmotes } from "../emotes/panel.tsx";
import { MinimapSlot, type HudMinimap } from "../minimap/minimap.tsx";
import { WorldMapFloat } from "../world-map/float-view.tsx";
import { WorldMapPopout } from "../world-map/popout-view.tsx";
import { WorldMapWindow, type HudWorldMap } from "../world-map/window.tsx";
import { ChestWindow, type HudChest } from "../chest/window.tsx";
import { PartyFrames } from "../party/frames.tsx";
import { PlayerMenu } from "../player-menu/menu.tsx";
import { InvitationPrompt } from "../party/prompt.tsx";
import type { HudParty } from "../party/hud-party.ts";
import { IconsProvider } from "../icons/context.tsx";
import { NO_ICONS, type IconSource } from "../icons/page-icons.ts";
import type { BarArranging } from "../bar/arranging.ts";
import { say } from "../../words.ts";
import { ownNow } from "../own-clock.ts";

/**
 * The Now HUD (P1.9-T13; ADR-061 and its amendments; the brief's HUD layout, message line and PvP flag
 * indicator): the player card at top left (the amendment "the player card": the flag mark, counting down the end of
 * a Combatant window, the character's name and level, and the health, Stamina and Focus bars), the target frame
 * beside it while the player holds a target (P2.2-T3, `target-frame.tsx`), the message line at bottom centre and the menu slot at
 * bottom right (P1.9-T14: the Esc key glyph and the menu button), each in its slot of `HUD_SLOTS` on the logical
 * area the store holds, with the diagnostic strip at bottom left when the page asks for it (`?diag`). It reads the
 * game's store through `useSyncExternalStore`, so it draws only when a value it shows changes, never on the
 * animation frame, and `onCommit` is told after each commit of its parts, which the page counts. The store's
 * snapshot also serves as the server snapshot, which only static rendering under `bun test` asks for.
 *
 * The XP bar sits above the ability bar once the first `progress` has come (P2.4-T3, `xp-bar.tsx`), the toast
 * column holds the maintenance warning's toast and the level-up toast (P2.4-T4, `toast-column.tsx`), and the stance
 * button sits right of the bar (P2.2-T13, `stance-button.tsx`).
 *
 * Nothing in it takes the pointer but the menu button, the player card's flag mark, which opens the flag toggle's
 * panel under the card (ADR-076 decision 6, ADR-061's note of 2026-09-28), the ability bar's slots (P2.2-T4,
 * `combat-hud.tsx`, with the cast bar and the effects rows) and the stance button (P2.2-T13): the rest of the card, the message line, the key glyph,
 * the rows and the strip take no pointer events, so a click on them or between them reaches the world.
 * The component imports no stylesheet, so it renders to static markup under `bun test`; `mount.tsx` imports
 * the sheets.
 */

/**
 * Tells the page each time the calling component commits: an effect with no dependencies runs after every
 * commit.
 *
 * @param onCommit What to tell, if anything.
 */
function useCommits(onCommit: (() => void) | undefined): void {
  useEffect(() => {
    onCommit?.();
  });
}

/**
 * The accessible values of a bar: a `meter` named by its word, from 0 to its maximum, with its value once the first
 * `vitals` has come.
 *
 * @param label The bar's accessible name.
 * @param bar The bar.
 * @returns The attributes.
 */
function meterOf(label: string, bar: VitalBar): Readonly<Record<string, string | number>> {
  return { role: "meter", "aria-label": label, "aria-valuemin": 0, "aria-valuemax": bar.max, ...(bar.now === null ? {} : { "aria-valuenow": bar.now }) };
}

/**
 * Keeps the card's flashes under an element in the page clock's phase (`holdFlashPhase`): after each commit in which
 * the flash's `beat` or reduced motion changed, before the browser paints, since that is when the sheet starts a flash
 * or starts it again, so a flash never shows a frame out of step.
 *
 * @param beat What the element flashes for: whether its bar is low, or its countdown's phase.
 * @param reduced Whether motion is reduced, under which the sheet stops the flash.
 * @returns The ref of the element whose flashes, its own and its descendants', are kept.
 */
function useFlashPhase(beat: boolean | string, reduced: boolean): RefObject<HTMLSpanElement | null> {
  const ref = useRef<HTMLSpanElement | null>(null);
  useLayoutEffect(() => {
    if (ref.current !== null) {
      holdFlashPhase(ref.current.getAnimations({ subtree: true }));
    }
  }, [beat, reduced]);
  return ref;
}

/**
 * The health bar (ADR-061, amendment "the player card"): the card's dominant bar, its fill cropped on whole logical
 * pixels to at least one column while any health remains (`healthFill`), and, unless the viewer hides health numbers,
 * the health and its maximum drawn in it in the kit's small digits, light with a dark outline, the outlined value
 * wholly inside the bar: an SVG of the digits' cells in logical pixels, scaled by the UI scale, so every cell lands
 * on whole device pixels. Its `data-value` holds the text; the bar's `aria-valuetext` says it. Below a tenth of the maximum it
 * carries `is-low`, and the sheet flashes its fill between red and the empty track (or, under reduced motion, holds
 * it amber); the flash is put on the page clock (`useFlashPhase`) when the bar turns low, and again each time reduced
 * motion changes, since the flash starts again when it runs again, so it is lit when `lowHealthLit` reads lit and
 * every low bar flashes together. Under a maximum-health penalty the value reads the current health over the full maximum, and the
 * part the penalty removes is a locked segment at the bar's right end (ADR-079's amendment of 2026-10-03 for P2.2-T16).
 *
 * @param props The `bar`, the card's content `width` and whether motion is `reduced`.
 * @returns The bar.
 */
function HealthBarView({ bar, width, reduced }: { readonly bar: HealthBar; readonly width: number; readonly reduced: boolean }): ReactElement {
  const fill = useFlashPhase(bar.low, reduced);
  return (
    <span className={`ot-hud-bar ot-hud-bar--health${bar.low ? " is-low" : ""}`} data-hud="health" {...meterOf(say("hud.vitals.health"), bar)} {...(bar.value === null ? {} : { "aria-valuetext": bar.value })} style={{ marginTop: logical(VITALS.rowGap), width: logical(width), height: logical(VITALS_CARD.health) }}>
      <span ref={fill} className="ot-hud-bar__fill" data-hud="health-fill" data-fill={bar.fill} data-low={bar.low ? "true" : undefined} style={{ width: logical(bar.fill) }} />
      {bar.pending === 0 ? null : <span className="ot-hud-bar__pending" data-hud="health-pending" data-fill={bar.pending} aria-hidden="true" style={{ left: logical(bar.fill), width: logical(bar.pending) }} />}
      {bar.locked === 0 ? null : <span className="ot-hud-bar__locked" data-hud="health-locked" data-fill={bar.locked} aria-hidden="true" style={{ width: logical(bar.locked) }} />}
      {bar.digits === null ? null : (
        <svg className="ot-hud-bar__value" data-hud="health-value" data-value={bar.value ?? ""} aria-hidden="true" viewBox={`0 0 ${bar.digits.width} ${bar.digits.height}`} shapeRendering="crispEdges" style={{ left: logical(bar.valueLeft), top: logical(VITALS_CARD.valueTop), width: logical(bar.digits.width), height: logical(bar.digits.height) }}>
          <path className="ot-hud-bar__value-outline" d={maskPath(bar.digits.outline)} />
          <path className="ot-hud-bar__value-glyphs" d={maskPath(bar.digits.glyph)} />
        </svg>
      )}
    </span>
  );
}

/**
 * The player card's flag mark, counting down the end of the player's Combatant window (ADR-073's amendment of
 * 2026-09-27, item 9, through `flagCountdown`): steady, then from 10 seconds before the end the Combatant mark
 * flickers, and from 5 seconds before it alternates with the mark of the state the window ends in, the second mark
 * hidden from assistive technology, until the snapshot's byte leaves Combatant. A flag switch whose byte carries the
 * switching bit counts down the same way (ADR-076's amendment of 2026-09-28): toward Protected the Flagged mark
 * flickers and then alternates with Protected, toward Flag on the two alternate throughout. Both effects are the
 * low-health flash's animation on its period, put on the page clock when the phase begins (`useFlashPhase`); the pair is
 * keyed by the phase, so both marks' animations start together at the phase's start and alternate in opposite halves
 * of each beat, exactly one mark showing at any moment. Under reduced motion the sheet stops them and the current mark
 * holds; they are put on the clock again when reduced motion changes, so they run again in phase. A timer wakes the mark at the next phase's start, so nothing runs on the animation frame.
 *
 * @param props The player's own `flag` byte, when its Combatant window ends on the page clock (`endsAt`, null for
 *   none), its PvP `preference`, its flag switch (`pvpSwitch`) and whether motion is `reduced`.
 * @returns The mark.
 */
function CardFlag({ flag, endsAt, preference, pvpSwitch, reduced }: { readonly flag: number; readonly endsAt: number | null; readonly preference: number; readonly pvpSwitch: HudSwitch; readonly reduced: boolean }): ReactElement {
  const [, wake] = useReducer((count: number) => count + 1, 0);
  const countdown = flagCountdown(ownNow(), endsAt, flag, preference, pvpSwitch);
  const { phase, next, changesInMs } = countdown;
  useEffect(() => {
    if (changesInMs === null) {
      return undefined;
    }
    const timer = setTimeout(wake, Math.max(0, changesInMs));
    return () => clearTimeout(timer);
  }, [changesInMs, phase]);
  const pair = useFlashPhase(phase, reduced);
  const { state, locked } = flagOf(flag);
  const mark = <FlagMark state={state} locked={locked} size={VITALS.flagSize} />;
  if (phase === "steady") {
    return mark;
  }
  return (
    <span key={phase} ref={pair} className={`ot-hud-countdown is-${phase}`} data-hud="flag-countdown" data-phase={phase}>
      <span className="ot-hud-countdown__now">{mark}</span>
      {phase === "alternate" ? (
        <span className="ot-hud-countdown__next" data-hud="flag-next" aria-hidden="true">
          <FlagMark state={next.state} locked={next.locked} size={VITALS.flagSize} />
        </span>
      ) : null}
    </span>
  );
}

/**
 * The flag switch's countdown in the card's header (ADR-076's note of 2026-10-03, `switchCountdown`): while a switch
 * counts, the panel's line for its seconds left ("Flagged in 4 s", "Protected in 4 s") in the name's place, whether or
 * not the panel is open, so a player who closed the panel still sees the switch running. It reads the switch the
 * server last reported, not the flag byte's switching bit, which a switch that leaves the mark as it is never sets. A
 * timer wakes it when the seconds change, so nothing runs on the animation frame; it has no motion, so reduced motion
 * leaves it as it is.
 *
 * @param props The player's flag `pvpSwitch`, counting.
 * @returns The line, or nothing once the switch has ended.
 */
function SwitchCountdown({ pvpSwitch }: { readonly pvpSwitch: HudSwitch }): ReactElement | null {
  const [, wake] = useReducer((count: number) => count + 1, 0);
  const countdown = switchCountdown(pvpSwitch, ownNow());
  const changesInMs = countdown?.changesInMs ?? null;
  useEffect(() => {
    if (changesInMs === null) {
      return undefined;
    }
    const timer = setTimeout(wake, Math.max(0, changesInMs));
    return () => clearTimeout(timer);
  }, [changesInMs, countdown?.line]);
  if (countdown === null) {
    return null;
  }
  return (
    <span className="ot-hud-vitals__switch" data-hud="switch-countdown" role="timer" style={{ marginLeft: logical(VITALS.nameGap), marginTop: logical(KIT_METRICS.flag[VITALS.flagSize].wordTop) }}>
      {countdown.line}
    </span>
  );
}

/**
 * A thin bar under the health bar: Stamina or Focus, a fill on whole logical pixels and no value.
 *
 * @param props The bar's `kind`, the `bar` and the card's content `width`.
 * @returns The bar.
 */
function ThinBarView({ kind, bar, width }: { readonly kind: "stamina" | "focus"; readonly bar: VitalBar; readonly width: number }): ReactElement {
  return (
    <span className={`ot-hud-bar ot-hud-bar--${kind}`} data-hud={kind} {...meterOf(say(kind === "stamina" ? "hud.vitals.stamina" : "hud.vitals.focus"), bar)} style={{ marginTop: logical(VITALS.rowGap), width: logical(width), height: logical(VITALS_CARD.thin) }}>
      <span className="ot-hud-bar__fill" data-hud={`${kind}-fill`} data-fill={bar.fill} style={{ width: logical(bar.fill) }} />
    </span>
  );
}

/**
 * The card's flag mark as the flag toggle's button (ADR-076 decision 6): named "PvP flag" with the flag's words, it
 * opens and closes the toggle's panel, and a pointer press on it keeps focus where it was, as the menu button's does.
 *
 * @param props The player's `flag` byte, the Combatant window's end, the `preference` and the `pvpSwitch` its countdown
 *   reads, whether motion is `reduced`, whether the panel is `open`, and `onToggle`, which opens or closes it.
 * @returns The button.
 */
function FlagButton({ flag, endsAt, preference, pvpSwitch, reduced, open, onToggle }: { readonly flag: number; readonly endsAt: number | null; readonly preference: number; readonly pvpSwitch: HudSwitch; readonly reduced: boolean; readonly open: boolean; readonly onToggle: () => void }): ReactElement {
  const { state, locked } = flagOf(flag);
  return (
    <button type="button" className="ot-hud-flag-button" data-hud="flag-button" aria-label={`${say("hud.flag.mark")}, ${flagWords(state, locked)}`} aria-expanded={open} onMouseDown={keepFocus} onClick={onToggle}>
      <CardFlag flag={flag} endsAt={endsAt} preference={preference} pvpSwitch={pvpSwitch} reduced={reduced} />
    </button>
  );
}

/**
 * The player card (ADR-061, amendment "the player card"): the header row of the flag mark at 8 (the locked variant
 * when an area holds the state, named by the flag's words, counting down a Combatant window's end through
 * `CardFlag`, and the flag toggle's button, ADR-076), the character's first name, the in-combat mark in the danger
 * colour while the `vitals` say in combat (P2.2-T1), in a slot the header keeps after the name whether or not the mark
 * shows, and the level at the right, the name and the level giving way to the switch's countdown while a flag switch
 * counts (`SwitchCountdown`, which fits the same room); then the health bar with its value and its regeneration
 * segment and the Stamina and Focus bars, in a HUD frame exactly as wide as its slot, which nothing it shows widens
 * (ADR-061's amendment of 2026-10-03, "the player card keeps one width"); and, while open, the flag toggle's panel
 * under it. The name is the player's own first name without its family, cut with U+2026 to the room the header leaves
 * it (`fitName`, `nameRoom`); the full name, first and family, is the name's tooltip in the HUD's tooltip (P2.8-T68), and, where the drawn name differs
 * from it, a copy hidden from sight that assistive technology reads in the drawn name's place. While the panel is open
 * the slot is raised on the HUD's stacking scale (`hud.css`, `--ot-hud-z-raised`), so the panel draws over the status
 * rows and anything else laid out after the card that it drops onto, which would otherwise paint over it in document
 * order.
 *
 * @param props The `area` it is laid out on, the character's full `name` and its `firstName`, the PvP `flag` byte,
 *   when the Combatant window ends (`endsAt`, null for none) and the PvP `preference`, the flag `pvpSwitch`, the
 *   `level` (null before the character's entity arrives), the player's `vitals` (null before the first), its effects' summed maximum-health
 *   modifier (`healthPercent`, which with the level's pool locks the part of the health bar a penalty removes), whether
 *   the viewer shows health `numbers`, whether motion is `reduced`, whether the toggle's panel is `open`, whether a key
 *   opened it (`byKey`) and `setOpen`, which the mark opens and closes it with, `onFlagToggle`, which the panel's button
 *   asks with, `onCommit`, and the page's `tips` controller (null where nothing shows tooltips).
 * @returns The card in its slot.
 */
const VitalsFrame = memo(function VitalsFrame({ area, name, firstName, flag, endsAt, preference, pvpSwitch, inParty, level, vitals, healthPercent, numbers, reduced, open, byKey, setOpen, onFlagToggle, onCommit, tips, onCharacter }: { readonly onCharacter?: (() => void) | undefined; readonly tips: TipController | null; readonly area: HudArea; readonly name: string; readonly firstName: string; readonly flag: number; readonly endsAt: number | null; readonly preference: number; readonly pvpSwitch: HudSwitch; readonly inParty: boolean; readonly level: number | null; readonly vitals: HudVitals | null; readonly healthPercent: number; readonly numbers: boolean; readonly reduced: boolean; readonly open: boolean; readonly byKey: boolean; readonly setOpen: (open: boolean) => void; readonly onFlagToggle: ((wanted: number) => void) | undefined; readonly onCommit: (() => void) | undefined }): ReactElement {
  useCommits(onCommit);
  const handlers = useTipHandlers(tips);
  const slot = resolveSlot(HUD_SLOTS.vitals, area);
  const levelWords = levelText(level);
  const width = VITALS_CARD.width;
  const shown = fitName(firstName, nameRoom(levelWords));
  const view = vitalsView(vitals, width, numbers, PAGE_RULES.resources.health, { pool: level === null ? null : levelRow(PAGE_RULES.tables.levels, level).pools.health, percent: healthPercent });
  const row = KIT_METRICS.flag[VITALS.flagSize];
  const counting = pvpSwitch.endsAt !== null;
  return (
    <div className={open ? "ot-hud-slot is-raised" : "ot-hud-slot"} data-hud-slot="vitals" style={{ left: logical(slot.left), top: logical(slot.top), width: logical(slot.width) }}>
      <HudFrame>
        <span className="ot-hud-vitals__header" data-hud="header" style={{ width: logical(width), height: logical(VITALS_CARD.header) }}>
          <span className="ot-hud-vitals__flag" data-hud="flag" style={{ marginTop: logical(row.markTop) }}>
            <FlagButton flag={flag} endsAt={endsAt} preference={preference} pvpSwitch={pvpSwitch} reduced={reduced} open={open} onToggle={() => setOpen(!open)} />
          </span>
          {counting ? (
            <SwitchCountdown pvpSwitch={pvpSwitch} />
          ) : (
            <>
              <span className="ot-hud-vitals__name" data-hud="name" data-tip={tips === null ? undefined : NAME_TIP_ANCHOR} tabIndex={tips === null || shown !== name ? undefined : 0} {...handlers} aria-hidden={shown === name ? undefined : true} style={{ marginLeft: logical(VITALS.nameGap), marginTop: logical(row.wordTop) }}>
                {shown}
              </span>
              {shown === name ? null : (
                <span className="ot-hud-vitals__spoken" data-hud="name-full">
                  {name}
                </span>
              )}
            </>
          )}
          <span className="ot-hud-vitals__combat-slot" data-hud="combat-slot" style={{ marginLeft: logical(VITALS.nameGap), marginTop: logical(row.markTop), width: logical(COMBAT_SLOT) }}>
            {view.inCombat ? (
              <span className="ot-hud-vitals__combat" data-hud="in-combat">
                <Glyph name="zap" label={say("hud.vitals.inCombat")} />
              </span>
            ) : null}
          </span>
          {levelWords === null || counting ? null : (
            <span className="ot-hud-vitals__level" data-hud="level" style={{ paddingLeft: logical(VITALS.levelGap), marginTop: logical(row.wordTop) }}>
              {levelWords}
            </span>
          )}
        </span>
        <HealthBarView bar={view.health} width={width} reduced={reduced} />
        <ThinBarView kind="stamina" bar={view.stamina} width={width} />
        <ThinBarView kind="focus" bar={view.focus} width={width} />
        {onCharacter === undefined ? null : <button type="button" className="ot-hud-vitals__open" data-hud="open-character" aria-label={say("character.title")} onClick={onCharacter} />}
      </HudFrame>
      {open ? <FlagTogglePanel width={width} state={{ flag, preference, combatantEndsAt: endsAt, inCombat: vitals?.inCombat ?? false, dead: vitals?.dead ?? false, pvpSwitch, lockedReason: flagLockReason({ level, inParty, preference }) }} byKey={byKey} onRequest={onFlagToggle} /> : null}
    </div>
  );
});

/**
 * The message line in its slot, growing upward from the slot's bottom to at most `MESSAGE_MAX_LINES` lines
 * and cut short with U+2026 past them. The prompt of a door entered with E, when there is one, is a line of its own in the
 * same column just over the message line's bottom row, so a reply neither replaces nor blanks it and a reply of several
 * lines pushes it up instead of being drawn under it.
 *
 * @param props The `area` it is laid out on, the line's `text` or null, the door `prompt` or null, `onInteract`, which a
 * press on the prompt calls as E does, and `onCommit`.
 * @returns The slot holding the line's live region and the prompt.
 */
const MessageSlot = memo(function MessageSlot({ area, text, prompt, onInteract, onCommit }: { readonly area: HudArea; readonly text: string | null; readonly prompt: string | null; readonly onInteract: (() => void) | undefined; readonly onCommit: (() => void) | undefined }): ReactElement {
  useCommits(onCommit);
  const slot = slotRect("message", area);
  return (
    <div className="ot-hud-slot ot-hud-slot--rising" data-hud-slot="message" style={{ left: logical(slot.left), top: logical(slot.top), width: logical(slot.width), height: logical(slot.height) }}>
      {prompt === null ? null : (
        <button type="button" className="ot-hud-prompt" data-hud="prompt" data-hud-slot="prompt" aria-label={prompt} onMouseDown={keepFocus} onClick={onInteract}>
          <MessageLine text={prompt} width={slot.width} maxLines={1} />
        </button>
      )}
      <div data-hud="reply" style={{ minHeight: logical(KIT_METRICS.message.height) }}>
        <MessageLine text={text} width={slot.width} maxLines={MESSAGE_MAX_LINES} />
      </div>
    </div>
  );
});

/**
 * Keeps a pointer press on the menu button from taking focus. A focused button answers Space and Enter, so a
 * button left focused after the menu closes would reopen it on a key the game captures; with this, a menu
 * opened by the pointer gives focus back to what had it before, and a menu opened from the button by the
 * keyboard, which reached it with Tab, gives focus back to the button.
 *
 * @param event The press.
 */
function keepFocus(event: MouseEvent<HTMLElement>): void {
  event.preventDefault();
}

/**
 * The menu slot: the Esc key glyph, which names the game menu's shortcut and takes no pointer events, and the
 * menu button, the menu's pointer and touch path (brief rule 23), each where `MENU_LAYOUT` puts it in the slot.
 *
 * @param props The `area` it is laid out on, `onMenu`, which the button calls to ask for the game menu (the
 *   gallery gives none), and `onCommit`.
 * @returns The slot holding the glyph and the button.
 */
const MenuSlot = memo(function MenuSlot({ area, onMenu, onCommit }: { readonly area: HudArea; readonly onMenu: (() => void) | undefined; readonly onCommit: (() => void) | undefined }): ReactElement {
  useCommits(onCommit);
  const slot = slotRect("menu", area);
  const touch = area.touch === true;
  const keyed = useSyncExternalStore(pageKeyHints.subscribe, pageKeyHints.getSnapshot, pageKeyHints.getSnapshot);
  const { key, button } = MENU_LAYOUT;
  return (
    <div className="ot-hud-slot" data-hud-slot="menu" style={{ left: logical(slot.left), top: logical(slot.top), width: logical(slot.width), height: logical(slot.height) }}>
      {touch && !keyed ? null : (
        <span className="ot-hud-menu__part" data-hud="menu-key" style={{ left: logical(key.left), top: logical(key.top) }}>
          <KeyGlyph>{MENU_KEY}</KeyGlyph>
        </span>
      )}
      <span className="ot-hud-menu__part" data-hud="menu-button" style={{ left: logical(touch ? 0 : button.left), top: logical(touch ? 0 : button.top) }} onMouseDown={keepFocus}>
        <IconButton {...(touch ? { art: { src: BUTTON_ICONS.menu, size: BUTTON_ICON_SIZE } } : { icon: "menu" as const })} label={say("hud.gameMenu")} {...(onMenu === undefined ? {} : { onClick: onMenu })} />
      </span>
    </div>
  );
});

/**
 * The Bag button of a touch page (ADR-061's amendment of 2026-10-31, decision 9): the briefcase, left of the menu button,
 * which opens and closes the Inventory window as its key does.
 *
 * @param props The `area` it is laid out on and `onToggle`, which the button calls.
 * @returns The button in its place.
 */
const BagButton = memo(function BagButton({ area, onToggle }: { readonly area: HudArea; readonly onToggle: (() => void) | undefined }): ReactElement {
  const rect = bagButtonRect(area);
  return (
    <div className="ot-hud-slot" data-hud-slot="bag-button" style={{ left: logical(rect.left), top: logical(rect.top), width: logical(rect.width), height: logical(rect.height) }} onMouseDown={keepFocus}>
      <IconButton art={{ src: BUTTON_ICONS.bag, size: BUTTON_ICON_SIZE }} label={say("hud.bagButton")} {...(onToggle === undefined ? {} : { onClick: onToggle })} />
    </div>
  );
});

/**
 * The diagnostic strip, for development only: the page's connection and measurements in one line of caption text at
 * bottom left, wrapping a gutter short of the ability bar as it stands on the area (`diagnosticsRoom`). It takes no
 * pointer events. A feed with no line, as on a page that is not a development page, where nothing reads one, draws
 * nothing.
 *
 * @param props The `area` it is laid out on and the `feed` of its line.
 * @returns The strip, or null while the feed holds no line.
 */
function DiagnosticsStrip({ area, feed }: { readonly area: HudArea; readonly feed: DiagnosticsSource }): ReactElement | null {
  const line = useSyncExternalStore(feed.subscribe, feed.getSnapshot, feed.getSnapshot);
  const room = diagnosticsRoom(area);
  if (line === "") {
    return null;
  }
  return (
    <div className="ot-hud-diag" data-hud="diag" style={{ maxWidth: logical(room.width) }}>
      {line}
    </div>
  );
}

/**
 * A message line that shows nothing, for a HUD given no way to say a line.
 */
const NO_NOTICE = (): void => undefined;

/**
 * The HUD.
 *
 * @param props The `store` it reads, the `diagnostics` feed when the page shows the strip (null otherwise),
 *   `onCommit`, told after each commit of the HUD's parts, `onMenu`, which the menu button calls to ask
 *   for the game menu, `onInteract`, which a press on the door's prompt calls as E does, `onFlagToggle`, which the flag toggle's button calls with the preference it asks for (answering whether it went; the invitation prompt's Switch and accept asks through it too), `onFlagNotice`, which shows a line on the message line,
 *   `onFlagPanel`, which the card's mark opens and closes the toggle's panel with, the panel then following the store's
 *   `flagPanel` (ADR-076's amendment of 2026-10-01: the U key shares it); without it the panel is the HUD's own state,
 *   starting open with `flagPanel` (the gallery's specimens and the tests); and the page's maintenance
 *   `warnings` with `onLogOut`, the game menu's Log out, which the last minute's Log out now runs (ADR-078); and the
 *   combat HUD's `bar` (P2.2-T4: the ability bar, the cast bar and the effects rows, `combat-hud.tsx`), its store,
 *   its ticker, its tooltips' controller (P2.2-T12, `bar/tooltip-view.tsx`) and its arranging (P2.8-T29,
 *   `bar/arranging.ts`), which the page and the gallery give and the other tests leave out; the `stance` the stance button reads and toggles (P2.2-T13), drawn only with it; and the
 *   performance panel's `perf` store (ADR-087), the panel drawn in its slot while the store says it shows; and the
 *   `bag` the Inventory window reads and acts on (P2.8-T11, `bag/window.tsx`), drawn while its store says it is open,
 *   under the HUD's one tooltip, which its slots share; and the `chest` the loot window reads and acts on (P2.8-T16,
 *   `chest/window.tsx`), drawn while its store holds a chest, with the `loot` toasts the toast column shows for what a
 *   take moved to the bag; and the page's `discoveries`, the discovery toasts the toast column draws under the
 *   level-up toast (P2.11-T8); and the `emotes` the emote wheel reads and asks (P2.19-T8, `emotes/panel.tsx`), drawn
 *   while its store says it is open; and the `minimap` (P1.9-T52, `minimap/minimap.tsx`), the field in the minimap slot with
 *   its controls and the Nearby window, drawn only with it; the `worldMap` (P2.12-T6, `world-map/window.tsx`), the map window, drawn while its store says it is open and shown as the window, or the float (P2.12-T23, `world-map/float-view.tsx`) while it is shown as that; and the page's `icons` (ADR-111), the pointer and atlas its item slots, the bar's
 *   item slot and the loot toasts draw colour art from, every glyph drawn without them.
 * @returns The HUD's layer, the size of the logical area, holding each surface in its slot.
 * @example
 * <Hud store={hud} diagnostics={null} onCommit={() => (gameReport.hudRenders += 1)} onMenu={() => menu.request()} onFlagToggle={(wanted) => hud.requestFlag(wanted)} />
 */
export function Hud({ store, diagnostics, onCommit, onMenu, onInteract, onFlagToggle, onFlagNotice, onFlagPanel, flagPanel = false, warnings, onLogOut, bar, levelUp, discoveries, stance, perf, bag, chest, loot, emotes, minimap, worldMap, party, harvest, icons, character }: { readonly character?: HudCharacter | undefined; readonly store: HudSource; readonly diagnostics: DiagnosticsSource | null; readonly onCommit?: (() => void) | undefined; readonly onMenu?: (() => void) | undefined; readonly onInteract?: (() => void) | undefined; readonly onFlagToggle?: ((wanted: number) => boolean | void) | undefined; readonly onFlagNotice?: ((line: string) => void) | undefined; readonly onFlagPanel?: ((open: boolean) => void) | undefined; readonly flagPanel?: boolean; readonly warnings?: MaintenanceWarnings | undefined; readonly onLogOut?: (() => void) | undefined; readonly bar?: { readonly source: BarSource; readonly ticker: BarTicker | null; readonly tips?: TipController | undefined; readonly arrange?: BarArranging | undefined } | undefined; readonly levelUp?: LevelUpToast | undefined; readonly discoveries?: DiscoveryToasts | undefined; readonly stance?: StanceSource | undefined; readonly perf?: PerfSource | undefined; readonly bag?: HudBag | undefined; readonly chest?: HudChest | undefined; readonly loot?: HudLoot | undefined; readonly emotes?: HudEmotes | undefined; readonly minimap?: HudMinimap | undefined; readonly worldMap?: HudWorldMap | undefined; readonly party?: HudParty | undefined; readonly harvest?: HudHarvest | undefined; readonly icons?: IconSource | undefined }): ReactElement {
  const snapshot = useSyncExternalStore(store.subscribe, store.getSnapshot, store.getSnapshot);
  const { name, firstName, flag, combatantEndsAt, preference, pvpSwitch, level, message, prompt, vitals, maxHealthPercent, healthNumbers, barKeys, reducedMotion, target, progress, connection, layout } = snapshot;
  const cardRight = name === "" ? 0 : resolveSlot(HUD_SLOTS.vitals, layout).left + framedWidth(VITALS_CARD.width);
  const [ownPanel, setOwnPanel] = useState(flagPanel);
  const panelOpen = onFlagPanel === undefined ? ownPanel : snapshot.flagPanel;
  const potionsHalved = vitals?.inPvpCombat ?? false;
  const tipTargetLeft = useMemo(() => targetRowLeft(layout, cardRight), [layout, cardRight]);
  const tipXp = useMemo(() => (progress === null ? null : { progress, flag }), [progress, flag]);
  const tipBag = useMemo(() => (bag === undefined ? null : { source: bag.source, rules: bag.rules, potionsHalved, place: bag.place, size: bag.size, wallet: bag.wallet }), [bag?.source, bag?.rules, potionsHalved, bag?.place, bag?.size, bag?.wallet]);
  useCommits(onCommit);
  return (
    <IconsProvider source={icons ?? NO_ICONS}>
    <TipsContext.Provider value={bar?.tips ?? null}>
    <div className="ot-hud-layer" data-hud="layer" style={{ width: logical(layout.width), height: logical(layout.height) }}>
      {name === "" ? null : <VitalsFrame area={layout} name={name} firstName={firstName} flag={flag} endsAt={combatantEndsAt} preference={preference} pvpSwitch={pvpSwitch} inParty={snapshot.inParty} level={level} vitals={vitals} healthPercent={maxHealthPercent} numbers={healthNumbers} reduced={reducedMotion} open={panelOpen} byKey={snapshot.flagPanelByKey} setOpen={onFlagPanel ?? setOwnPanel} onFlagToggle={onFlagToggle} onCommit={onCommit} tips={bar?.tips ?? null} onCharacter={character === undefined ? undefined : () => character.source.toggle()} />}
      {target === null ? null : <TargetFrame area={layout} target={target} numbers={healthNumbers} after={cardRight} onCommit={onCommit} />}
      {party === undefined || name === "" ? null : <PartyFrames area={layout} party={party.source} />}
      {bar === undefined || name === "" ? null : <StatusRows area={layout} source={bar.source} ticker={bar.ticker} targeted={target !== null} held={statusUnderMark(connection).held} drop={statusUnderMark(connection).drop} after={cardRight} tips={bar.tips ?? null} onCommit={onCommit} />}
      {name === "" ? null : <ConnectionMark area={layout} connection={connection} />}
      {bar === undefined || name === "" ? null : <CastBar area={layout} source={bar.source} onCommit={onCommit} />}
      {worldMap?.travel === undefined || name === "" ? null : <TravelBar area={layout} travel={worldMap.travel} />}
      {harvest === undefined || name === "" ? null : <HarvestBar area={layout} harvest={harvest} />}
      {bar === undefined || name === "" ? null : <AbilityBar area={layout} source={bar.source} ticker={bar.ticker} tips={bar.tips ?? null} keys={barKeys} onCommit={onCommit} arrange={bar.arrange ?? null} />}
      {stance === undefined || name === "" || !slotDrawn("stance", layout) ? null : <StanceButton area={layout} source={stance} tips={bar?.tips ?? null} showKey={barKeys} onCommit={onCommit} />}
      {bar?.arrange?.belt == null || name === "" || !(slotDrawn("tools", layout) || layout.touch === true) ? null : <ToolBeltView area={layout} belt={bar.arrange.belt} rules={bar.source.rules} showKey={barKeys} onCommit={onCommit} />}
      {bag === undefined || name === "" ? null : <BagWindow area={layout} bag={bag} tips={bar?.tips ?? null} potionsHalved={potionsHalved} />}
      {chest === undefined || name === "" ? null : <ChestWindow area={layout} chest={chest} />}
      {character === undefined || name === "" ? null : <CharacterWindow area={layout} character={character} header={{ name, level: level ?? 1 }} />}
      {emotes === undefined || name === "" ? null : <EmoteWheel area={layout} emotes={emotes} target={target === null ? null : targetFrameRect(layout, target, cardRight)} />}
      {bar?.tips === undefined || name === "" ? null : <HudTooltip cardName={name} area={layout} source={bar.source} tips={bar.tips} ticker={bar.ticker} targetLeft={tipTargetLeft} xp={tipXp} stance={stance ?? null} bag={tipBag} />}
      {progress === null || name === "" ? null : <XpBar area={layout} progress={progress} levelUp={levelUp} tips={bar?.tips ?? null} onCommit={onCommit} />}
      <MessageSlot area={layout} text={messageShown(message, vitals)} prompt={prompt} onInteract={onInteract} onCommit={onCommit} />
      <MenuSlot area={layout} onMenu={onMenu} onCommit={onCommit} />
      {layout.touch !== true || bag === undefined || name === "" ? null : <BagButton area={layout} onToggle={bag.onToggle} />}
      {warnings === undefined ? null : <MaintenanceSlots area={layout} warnings={warnings} onLogOut={onLogOut} />}
      <ToastColumn area={layout} warnings={warnings} levelUp={levelUp} discoveries={discoveries} loot={loot} onCommit={onCommit} />
      {diagnostics === null ? null : <DiagnosticsStrip area={layout} feed={diagnostics} />}
      {minimap === undefined || name === "" ? null : <MinimapSlot area={layout} minimap={minimap} />}
      {worldMap === undefined || name === "" ? null : <WorldMapWindow area={layout} map={worldMap} minimap={minimap} />}
      {worldMap === undefined || name === "" ? null : <WorldMapFloat area={layout} map={worldMap} minimap={minimap} />}
      {worldMap === undefined || name === "" ? null : <WorldMapPopout map={worldMap} minimap={minimap} />}
      {party === undefined || name === "" ? null : <InvitationPrompt area={layout} party={party.source} warnings={warnings} hud={onFlagToggle === undefined ? undefined : { source: store, request: (wanted) => onFlagToggle(wanted) !== false, notice: onFlagNotice ?? NO_NOTICE }} />}
      {party?.input === undefined || party.menu === undefined || name === "" ? null : <PlayerMenu party={party.source} chat={party.menu.chat} actions={party.menu.actions} input={party.input} />}
      {perf === undefined ? null : <PerfPanel area={layout} source={perf} />}
    </div>
    </TipsContext.Provider>
    </IconsProvider>
  );
}
